import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const MealCategoryCard = ({ 
  category, 
  meals, 
  targetCalories, 
  consumedCalories,
  onAddFood,
  onViewDetails 
}) => {
  const progress = targetCalories > 0 ? (consumedCalories / targetCalories) * 100 : 0;
  const remaining = targetCalories - consumedCalories;

  const categoryIcons = {
    breakfast: 'Coffee',
    lunch: 'Utensils',
    dinner: 'UtensilsCrossed',
    snacks: 'Cookie'
  };

  const categoryColors = {
    breakfast: 'bg-warning/10 text-warning',
    lunch: 'bg-success/10 text-success',
    dinner: 'bg-primary/10 text-primary',
    snacks: 'bg-accent/10 text-accent'
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden transition-smooth hover-lift">
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center h-10 w-10 md:h-12 md:w-12 rounded-lg ${categoryColors?.[category?.toLowerCase()]}`}>
              <Icon 
                name={categoryIcons?.[category?.toLowerCase()]} 
                size={20} 
                strokeWidth={2}
              />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground capitalize">
                {category}
              </h3>
              <p className="text-xs md:text-sm caption text-muted-foreground">
                {meals?.length} item{meals?.length !== 1 ? 's' : ''} logged
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="Plus"
            onClick={() => onAddFood(category)}
            className="press-scale"
          >
            Add
          </Button>
        </div>

        {meals?.length > 0 ? (
          <div className="space-y-3 mb-4">
            {meals?.map((meal) => (
              <div 
                key={meal?.id}
                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
              >
                <div className="flex-shrink-0 h-12 w-12 md:h-14 md:w-14 rounded-lg overflow-hidden bg-background">
                  <Image
                    src={meal?.image}
                    alt={meal?.imageAlt}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-base font-medium text-foreground truncate">
                    {meal?.name}
                  </p>
                  <p className="text-xs md:text-sm caption text-muted-foreground">
                    {meal?.serving}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm md:text-base font-semibold data-text text-foreground">
                    {meal?.calories}
                  </p>
                  <p className="text-xs caption text-muted-foreground">kcal</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-6 text-center">
            <Icon 
              name="Salad" 
              size={32} 
              className="mx-auto mb-2 text-muted-foreground opacity-50"
            />
            <p className="text-sm text-muted-foreground">No meals logged yet</p>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs md:text-sm">
            <span className="caption text-muted-foreground">Calories</span>
            <span className="font-medium data-text text-foreground">
              {consumedCalories} / {targetCalories} kcal
            </span>
          </div>
          <div className="relative h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className={`absolute top-0 left-0 h-full rounded-full transition-smooth ${
                progress > 100 ? 'bg-error' : 'bg-success'
              }`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-xs caption ${remaining >= 0 ? 'text-success' : 'text-error'}`}>
              {remaining >= 0 ? `${remaining} kcal remaining` : `${Math.abs(remaining)} kcal over`}
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={() => onViewDetails(category)}
              className="h-auto p-0"
            >
              View Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealCategoryCard;